/**
 * Hides invalid variant option combinations and reconciles dependent selections
 * before the theme variant-picker fetch runs (capture-phase change handler).
 *
 * Each `variant-picker` keeps its own variant list. Hide rules are scoped to that
 * picker. A combined-listing product change reloads variants before reconcile,
 * so the previous product's matrix cannot check a different option.
 * `previewReconciledOptionValueIds` lets the picker prefetch the combination
 * the click will request, including the auto-selected dependent option.
 */
(function () {
  if (document.documentElement.dataset.reduceVariantsInit === 'true') return;
  document.documentElement.dataset.reduceVariantsInit = 'true';

  /**
   * @typedef {{ option1: string | null, option2: string | null, option3: string | null, available: boolean }} VariantRecord
   * @typedef {HTMLFieldSetElement | HTMLElement} OptionGroup
   * @typedef {HTMLInputElement | HTMLOptionElement} OptionControl
   * @typedef {{
   *   picker: HTMLElement,
   *   variants: VariantRecord[],
   *   productUrl: string,
   *   reloadToken: number,
   *   applying: boolean,
   *   frame: number | null,
   *   observer: MutationObserver | null,
   *   onChange: (event: Event) => void,
   * }} PickerState
   */

  /** @type {Map<HTMLElement, PickerState>} */
  const pickers = new Map();

  /** @type {HTMLStyleElement | null} */
  let styleElement = null;

  /** Last stylesheet text. Skipping an identical write avoids a style recalc after morph. */
  let appliedCss = '';

  /**
   * @param {string} value
   * @returns {string}
   */
  function cssAttr(value) {
    return String(value).replace(/\\/g, '\\\\').replace(/"/g, '\\"');
  }

  /**
   * @param {HTMLElement} picker
   * @returns {string}
   */
  function scopeSelector(picker) {
    const sectionId = picker.getAttribute('data-section-id');
    const blockId = picker.getAttribute('data-block-id');
    const productId = picker.getAttribute('data-product-id');

    if (sectionId && blockId && productId) {
      return `variant-picker[data-section-id="${cssAttr(sectionId)}"][data-block-id="${cssAttr(blockId)}"][data-product-id="${cssAttr(productId)}"]`;
    }

    if (!picker.dataset.reduceVariants) {
      picker.dataset.reduceVariants = `rv-${pickers.size + 1}`;
    }

    return `variant-picker[data-reduce-variants="${cssAttr(picker.dataset.reduceVariants)}"]`;
  }

  /**
   * @param {string | undefined} productUrl
   * @returns {Promise<VariantRecord[]>}
   */
  async function fetchVariants(productUrl) {
    if (!productUrl) return [];

    try {
      const response = await fetch(`${productUrl.split('?')[0]}.js`);
      if (!response.ok) return [];
      const data = await response.json();
      return Array.isArray(data.variants) ? data.variants : [];
    } catch {
      return [];
    }
  }

  /**
   * @param {HTMLElement} picker
   * @returns {OptionGroup[]}
   */
  function getGroups(picker) {
    return Array.from(
      picker.querySelectorAll(
        'fieldset.variant-option[data-fieldset-index], .variant-option--dropdowns[data-fieldset-index]'
      )
    ).sort((a, b) => Number(a.dataset.fieldsetIndex) - Number(b.dataset.fieldsetIndex));
  }

  /**
   * @param {OptionGroup} group
   * @returns {OptionControl | null}
   */
  function getSelectedControl(group) {
    if (group instanceof HTMLFieldSetElement) {
      const checked = group.querySelector('input[type="radio"]:checked');
      return checked instanceof HTMLInputElement ? checked : null;
    }

    const select = group.querySelector('select');
    if (!(select instanceof HTMLSelectElement)) return null;
    const selected = select.selectedOptions[0];
    return selected instanceof HTMLOptionElement ? selected : null;
  }

  /**
   * @param {OptionGroup} group
   * @param {string} optionValueId
   * @returns {OptionControl | null}
   */
  function controlForOptionValueId(group, optionValueId) {
    const found = group.querySelector(`[data-option-value-id="${CSS.escape(optionValueId)}"]`);
    if (found instanceof HTMLInputElement || found instanceof HTMLOptionElement) return found;
    return null;
  }

  /**
   * @param {OptionGroup} group
   * @param {string} value
   * @returns {OptionControl | null}
   */
  function controlForValue(group, value) {
    if (group instanceof HTMLFieldSetElement) {
      for (const input of group.querySelectorAll('input[type="radio"]')) {
        if (input instanceof HTMLInputElement && input.value === value) return input;
      }
      return null;
    }

    const select = group.querySelector('select');
    if (!(select instanceof HTMLSelectElement)) return null;

    for (const option of select.options) {
      if (option.value === value) return option;
    }

    return null;
  }

  /**
   * @param {VariantRecord} variant
   * @param {Record<number, string>} selections
   * @param {number} targetIndex
   * @param {string} targetValue
   */
  function variantMatches(variant, selections, targetIndex, targetValue) {
    const combined = { ...selections, [targetIndex]: targetValue };
    if (combined[0] != null && variant.option1 !== combined[0]) return false;
    if (combined[1] != null && variant.option2 !== combined[1]) return false;
    if (combined[2] != null && variant.option3 !== combined[2]) return false;
    return true;
  }

  /**
   * @param {PickerState} state
   * @param {Record<number, string>} selections
   * @param {number} targetIndex
   * @param {string} targetValue
   */
  function combinationExists(state, selections, targetIndex, targetValue) {
    return state.variants.some((variant) => variantMatches(variant, selections, targetIndex, targetValue));
  }

  /**
   * @param {PickerState} state
   * @param {Record<number, string>} selections
   * @param {number} targetIndex
   * @param {string} targetValue
   */
  function combinationAvailable(state, selections, targetIndex, targetValue) {
    return state.variants.some(
      (variant) => variantMatches(variant, selections, targetIndex, targetValue) && variant.available
    );
  }

  /**
   * @param {PickerState} state
   * @param {OptionGroup} group
   * @param {Record<number, string>} priorSelections
   * @returns {OptionControl | null}
   */
  function findBestControl(state, group, priorSelections) {
    const optionIndex = Number(group.dataset.fieldsetIndex);
    if (Number.isNaN(optionIndex)) return null;

    const controls =
      group instanceof HTMLFieldSetElement
        ? group.querySelectorAll('input[type="radio"]')
        : group.querySelector('select')?.options;

    if (!controls) return null;

    /** @type {OptionControl | null} */
    let fallback = null;

    for (const control of controls) {
      if (!(control instanceof HTMLInputElement || control instanceof HTMLOptionElement) || !control.value) continue;
      if (!combinationExists(state, priorSelections, optionIndex, control.value)) continue;
      if (!fallback) fallback = control;
      if (combinationAvailable(state, priorSelections, optionIndex, control.value)) return control;
    }

    return fallback;
  }

  /**
   * @param {HTMLElement} picker
   * @param {OptionGroup} group
   * @param {OptionControl | null} nextControl
   * @returns {boolean} Whether the selected control changed
   */
  function applySelection(picker, group, nextControl) {
    if (group instanceof HTMLFieldSetElement) {
      const current = group.querySelector('input[type="radio"]:checked');
      const currentInput = current instanceof HTMLInputElement ? current : null;
      const nextInput = nextControl instanceof HTMLInputElement ? nextControl : null;
      if (currentInput === nextInput) return false;
      if (currentInput) currentInput.checked = false;

      if (nextInput) {
        nextInput.checked = true;

        try {
          if (typeof picker.updateSelectedOption === 'function') picker.updateSelectedOption(nextInput);
        } catch (error) {
          console.error(error);
        }
      }

      return true;
    }

    const select = group.querySelector('select');
    if (!(select instanceof HTMLSelectElement) || !(nextControl instanceof HTMLOptionElement)) return false;
    if (select.selectedOptions[0] === nextControl) return false;

    for (const option of select.options) option.removeAttribute('selected');
    nextControl.selected = true;
    nextControl.setAttribute('selected', 'selected');
    select.value = nextControl.value;

    try {
      if (typeof picker.updateSelectedOption === 'function') picker.updateSelectedOption(select);
    } catch (error) {
      console.error(error);
    }

    return true;
  }

  /**
   * @param {PickerState} state
   * @param {number} changedOptionIndex
   */
  function reconcileFrom(state, changedOptionIndex) {
    if (!state.variants.length) return;

    /** @type {Record<number, string>} */
    const selections = {};

    for (const group of getGroups(state.picker)) {
      const index = Number(group.dataset.fieldsetIndex);
      if (Number.isNaN(index)) continue;

      const current = getSelectedControl(group);

      if (index <= changedOptionIndex) {
        if (current?.value) selections[index] = current.value;
        continue;
      }

      const currentValue = current?.value;
      if (currentValue && combinationExists(state, selections, index, currentValue)) {
        selections[index] = currentValue;
        continue;
      }

      const pick = findBestControl(state, group, selections);
      applySelection(state.picker, group, pick);
      if (pick?.value) selections[index] = pick.value;
    }
  }

  /**
   * @param {PickerState} state
   */
  function reconcileAll(state) {
    if (!state.variants.length) return;
    reconcileFrom(state, -1);
  }

  /**
   * @param {PickerState} state
   * @param {string[]} ids
   * @returns {string[]}
   */
  function previewReconciledOptionValueIds(state, ids) {
    if (!state.variants.length || !ids.length) return ids;

    const groups = getGroups(state.picker);
    if (groups.length !== ids.length) return ids;

    /** @type {(OptionControl | null)[]} */
    const hovered = ids.map((id, index) => controlForOptionValueId(groups[index], id));
    /** @type {number | null} */
    let changedIndex = null;

    for (let index = 0; index < groups.length; index++) {
      if (getSelectedControl(groups[index]) !== hovered[index]) {
        changedIndex = Number(groups[index].dataset.fieldsetIndex);
        break;
      }
    }

    if (changedIndex == null || Number.isNaN(changedIndex)) return ids;

    /** @type {Record<number, string>} */
    const selections = {};

    for (const group of groups) {
      const index = Number(group.dataset.fieldsetIndex);
      if (Number.isNaN(index)) continue;

      const groupPosition = groups.indexOf(group);
      const candidate = hovered[groupPosition]?.value;

      if (index <= changedIndex) {
        if (candidate) selections[index] = candidate;
        continue;
      }

      if (candidate && combinationExists(state, selections, index, candidate)) {
        selections[index] = candidate;
        continue;
      }

      const pick = findBestControl(state, group, selections);
      if (pick?.value) selections[index] = pick.value;
    }

    /** @type {string[]} */
    const result = [];

    for (const group of groups) {
      const index = Number(group.dataset.fieldsetIndex);
      const value = selections[index];
      const control = value ? controlForValue(group, value) : null;
      const optionValueId = control?.dataset.optionValueId;
      if (!optionValueId) return ids;
      result.push(optionValueId);
    }

    return result;
  }

  /**
   * @param {PickerState} state
   * @returns {string}
   */
  function buildCSS(state) {
    if (!state.variants.length) return '';

    const selections = {};
    for (const group of getGroups(state.picker)) {
      const index = Number(group.dataset.fieldsetIndex);
      const selected = getSelectedControl(group);
      if (!Number.isNaN(index) && selected?.value) selections[index] = selected.value;
    }

    const scope = scopeSelector(state.picker);
    let css = '';

    for (const group of getGroups(state.picker)) {
      if (!(group instanceof HTMLFieldSetElement)) continue;

      const index = Number(group.dataset.fieldsetIndex);
      if (Number.isNaN(index)) continue;

      /** @type {Record<number, string>} */
      const priorSelections = {};
      for (const [key, value] of Object.entries(selections)) {
        if (Number(key) < index) priorSelections[Number(key)] = value;
      }

      for (const input of group.querySelectorAll('input[type="radio"]')) {
        if (!(input instanceof HTMLInputElement) || !input.value) continue;
        const escaped = CSS.escape(input.value);
        const rule = `${scope} fieldset.variant-option[data-fieldset-index="${index}"] label:has(input[value="${escaped}"])`;

        if (!combinationExists(state, priorSelections, index, input.value)) {
          css += `${rule} { display: none !important; }\n`;
          continue;
        }

        if (!combinationAvailable(state, priorSelections, index, input.value)) {
          css += `${rule} { opacity: 0.9 !important; }\n`;
        }
      }
    }

    return css;
  }

  /**
   * Invalid dropdown options are hidden with the `hidden` attribute. CSS `display`
   * on `<option>` is unreliable, and these attributes are not part of the button CSS.
   * @param {PickerState} state
   */
  function applyDropdownVisibility(state) {
    if (!state.variants.length) return;

    for (const group of getGroups(state.picker)) {
      if (group instanceof HTMLFieldSetElement) continue;

      const select = group.querySelector('select');
      if (!(select instanceof HTMLSelectElement)) continue;

      const index = Number(group.dataset.fieldsetIndex);
      if (Number.isNaN(index)) continue;

      /** @type {Record<number, string>} */
      const priorSelections = {};
      for (const prior of getGroups(state.picker)) {
        const priorIndex = Number(prior.dataset.fieldsetIndex);
        const selected = getSelectedControl(prior);
        if (!Number.isNaN(priorIndex) && priorIndex < index && selected?.value) {
          priorSelections[priorIndex] = selected.value;
        }
      }

      for (const option of select.options) {
        if (!option.value) continue;
        const exists = combinationExists(state, priorSelections, index, option.value);

        if (!exists) {
          option.hidden = true;
          option.setAttribute('data-reduce-hidden', '');
        } else if (option.hasAttribute('data-reduce-hidden')) {
          option.hidden = false;
          option.removeAttribute('data-reduce-hidden');
        }
      }
    }
  }

  function applyFilters() {
    if (!styleElement) {
      styleElement = document.createElement('style');
      styleElement.id = 'variant-filter-css';
      document.head.appendChild(styleElement);
    }

    let css = '';
    for (const state of pickers.values()) css += buildCSS(state);
    if (css === appliedCss) return;

    appliedCss = css;
    styleElement.textContent = css;
  }

  /**
   * @param {PickerState} state
   */
  function runSync(state) {
    if (!state.picker.isConnected || !state.variants.length || state.applying) return;

    state.applying = true;
    reconcileAll(state);
    applyDropdownVisibility(state);
    applyFilters();
    state.applying = false;
  }

  /**
   * @param {PickerState} state
   */
  async function reloadAndSync(state) {
    const url = state.picker.dataset.productUrl || '';
    const token = ++state.reloadToken;
    const variants = await fetchVariants(url);

    if (!state.picker.isConnected || token !== state.reloadToken) return;
    if ((state.picker.dataset.productUrl || '') !== url) return;

    state.productUrl = url;
    state.variants = variants;
    runSync(state);
  }

  /**
   * @param {PickerState} state
   */
  function scheduleMorphSync(state) {
    if (state.frame != null) cancelAnimationFrame(state.frame);

    state.frame = requestAnimationFrame(() => {
      state.frame = null;
      if (!state.picker.isConnected) return;

      const url = state.picker.dataset.productUrl || '';
      if (url !== state.productUrl) {
        state.variants = [];
        clearDropdownHides(state.picker);
        applyFilters();
        void reloadAndSync(state);
        return;
      }

      runSync(state);
    });
  }

  /**
   * @param {PickerState} state
   * @param {Event} event
   */
  function handleChange(state, event) {
    const target = event.target;
    /** @type {Element | null} */
    let group = null;

    if (target instanceof HTMLInputElement && target.type === 'radio') {
      group = target.closest('fieldset.variant-option[data-fieldset-index]');
    } else if (target instanceof HTMLSelectElement) {
      group = target.closest('.variant-option--dropdowns[data-fieldset-index]');
    }

    if (!(group instanceof HTMLElement)) return;

    const changedIndex = Number(group.dataset.fieldsetIndex);
    if (Number.isNaN(changedIndex)) return;

    reconcileFrom(state, changedIndex);
    applyDropdownVisibility(state);
    applyFilters();
  }

  /**
   * @param {HTMLElement} picker
   */
  function clearDropdownHides(picker) {
    for (const option of picker.querySelectorAll('option[data-reduce-hidden]')) {
      if (!(option instanceof HTMLOptionElement)) continue;
      option.hidden = false;
      option.removeAttribute('data-reduce-hidden');
    }
  }

  /**
   * @param {HTMLElement} picker
   */
  function unbind(picker) {
    const state = pickers.get(picker);
    if (!state) return;
    state.observer?.disconnect();
    if (state.frame != null) cancelAnimationFrame(state.frame);
    state.reloadToken += 1;
    picker.removeEventListener('change', state.onChange, true);
    delete picker.previewReconciledOptionValueIds;
    clearDropdownHides(picker);
    pickers.delete(picker);
    applyFilters();
  }

  /**
   * @param {HTMLElement} picker
   */
  function bindPicker(picker) {
    if (pickers.has(picker)) return;

    /** @type {PickerState} */
    const state = {
      picker,
      variants: [],
      productUrl: '',
      reloadToken: 0,
      applying: false,
      frame: null,
      observer: null,
      onChange: (event) => handleChange(state, event),
    };

    pickers.set(picker, state);
    picker.previewReconciledOptionValueIds = (ids) => previewReconciledOptionValueIds(state, ids);
    picker.addEventListener('change', state.onChange, true);

    state.observer = new MutationObserver((mutations) => {
      if (state.applying) return;
      if (!mutations.some((mutation) => mutation.type === 'childList')) return;
      scheduleMorphSync(state);
    });
    state.observer.observe(picker, { childList: true, subtree: true });

    void reloadAndSync(state);
  }

  function scan() {
    for (const picker of [...pickers.keys()]) {
      if (!picker.isConnected) unbind(picker);
    }

    for (const node of document.querySelectorAll('variant-picker[data-product-url]')) {
      if (node instanceof HTMLElement) bindPicker(node);
    }
  }

  /**
   * Quick-add and other injected pickers are not on the page at startup.
   * Bind on press, before the picker's own pointerdown prefetch reads option ids.
   * @param {Event} event
   */
  function bindPickerFromPress(event) {
    if (!(event.target instanceof Element)) return;
    const picker = event.target.closest('variant-picker[data-product-url]');
    if (!(picker instanceof HTMLElement)) return;
    bindPicker(picker);
  }

  function start() {
    scan();
    document.addEventListener('pointerdown', bindPickerFromPress, true);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }

  document.addEventListener('shopify:section:load', () => {
    scan();
  });
})();
